import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatMoney } from '../ui/format';
import { Badge, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { endContract, listContracts, type Contract } from './api';

/**
 * Contracts.
 *
 * What was signed, for how long, and when it comes up — the three facts the CS account reads from
 * here. Ending one early asks for a reason, and the reason becomes the account's churn reason: a
 * contract that ends and an account that says "pending renewal" are two screens disagreeing.
 */
export function ContractsPage(): React.JSX.Element {
  const client = useQueryClient();
  const [ending, setEnding] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const contracts = useQuery({ queryKey: ['contracts'], queryFn: () => listContracts() });

  const end = useMutation({
    mutationFn: (id: string) => endContract(id, { status: 'CANCELED', reason: reason.trim() }),
    onSuccess: () => {
      setError(null);
      setEnding(null);
      setReason('');
      void client.invalidateQueries({ queryKey: ['contracts'] });
      void client.invalidateQueries({ queryKey: ['cs-accounts'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<Contract>[] = [
    {
      key: 'number',
      header: '#',
      align: 'end',
      render: (contract) => <span className="muted">{contract.number}</span>,
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (contract) =>
        `${t(`contracts.status.${contract.status}`)} ${contract.endReason ?? ''}`.trim(),
      render: (contract) => (
        <span className="cell-stack">
          <Badge tone={contract.status === 'ACTIVE' ? 'positive' : 'neutral'}>
            {t(`contracts.status.${contract.status}`)}
          </Badge>
          {contract.endReason && <span className="muted small">{contract.endReason}</span>}
        </span>
      ),
    },
    {
      key: 'total',
      header: t('proposals.total'),
      align: 'end',
      text: (contract) => formatMoney(contract.totalCents),
      render: (contract) => formatMoney(contract.totalCents),
    },
    {
      key: 'monthly',
      header: t('cs.mrr'),
      align: 'end',
      text: (contract) => formatMoney(contract.monthlyCents),
      render: (contract) => formatMoney(contract.monthlyCents),
    },
    {
      key: 'period',
      header: t('contracts.period'),
      text: (contract) => periodOf(contract),
      render: (contract) => <span className="muted small">{periodOf(contract)}</span>,
    },
    {
      key: 'renewal',
      header: t('cs.renewal'),
      text: (contract) => (contract.renewalDate ? formatDate(contract.renewalDate) : '—'),
      render: (contract) => (
        <span className="muted small">
          {contract.renewalDate ? formatDate(contract.renewalDate) : '—'}
        </span>
      ),
    },
    // Off by default; the chooser turns them on and the account remembers the choice.
    {
      key: 'term',
      header: t('proposals.term'),
      hidden: true,
      text: (contract) => t('proposals.months').replace('{n}', String(contract.termMonths)),
      render: (contract) => t('proposals.months').replace('{n}', String(contract.termMonths)),
    },
    {
      key: 'signedAt',
      header: t('contracts.signedAt'),
      hidden: true,
      text: (contract) => formatDate(contract.signedAt),
      render: (contract) => formatDate(contract.signedAt),
    },
    {
      key: 'endedAt',
      header: t('contracts.endedAt'),
      hidden: true,
      text: (contract) => formatDate(contract.endedAt),
      render: (contract) => formatDate(contract.endedAt),
    },
    {
      key: 'currency',
      header: t('record.field.currency'),
      hidden: true,
      render: (contract) => contract.currency,
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (contract) => (
        <>
          <span className="cell-inline">
            <Link
              className="button button--quiet button--compact"
              to={`/vendas/propostas/${contract.proposalId}`}
            >
              {t('contracts.openProposal')}
            </Link>
            {contract.status === 'ACTIVE' && ending !== contract.id && (
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setEnding(contract.id)}
              >
                {t('contracts.end')}
              </button>
            )}
          </span>

          {ending === contract.id && (
            <div className="cell-inline" style={{ marginTop: '8px' }}>
              <input
                className="input"
                value={reason}
                placeholder={t('contracts.endReason')}
                maxLength={1000}
                onChange={(event) => setReason(event.target.value)}
              />
              <button
                type="button"
                className="button button--compact"
                disabled={reason.trim() === '' || end.isPending}
                onClick={() => end.mutate(contract.id)}
              >
                {t('contracts.confirmEnd')}
              </button>
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setEnding(null)}
              >
                {t('confirm.cancel')}
              </button>
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="view view--wide">
      <PageHeader title={t('nav.contracts')} description={t('contracts.description')} />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel>
        <DataTable
          tableKey="contracts"
          rows={contracts.data?.contracts}
          columns={columns}
          rowKey={(contract) => contract.id}
          isLoading={contracts.isLoading}
          error={contracts.error}
          onRetry={() => void contracts.refetch()}
          empty={{ title: t('contracts.none') }}
        />
      </Panel>
    </div>
  );
}

/** Start and end as one string, so the filter matches the dates a person can read in the cell. */
function periodOf(contract: Contract): string {
  return `${formatDate(contract.startsOn)}${
    contract.endsOn ? ` → ${formatDate(contract.endsOn)}` : ''
  }`;
}
